import dayjs from 'dayjs';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { toast } from '../components/snackbar';
import { allLangs } from './all-langs';
import { fallbackLng, changeLangMessages as messages } from './locales-config';
import type { LanguageValue } from './locales-config';

/**
 * Custom hook for internationalization using i18next.
 *
 * This hook provides translation functions and language management capabilities.
 * It wraps the i18next's useTranslation hook and adds additional functionality
 * for language switching with toast notifications and dayjs locale management.
 *
 * @param ns - Optional namespace for translations
 *
 * @returns An object containing:
 * - t: Translation function from i18next
 * - i18n: The i18n instance from i18next
 * - onChangeLang: Function to change the current language with toast notifications
 * - currentLang: The current language object with value, label, and other properties,
 *                falling back to the default language if current is undefined
 *
 * @example
 * ```tsx
 * const { t, currentLang, onChangeLang } = useTranslate('common');
 *
 * // Use translation function
 * const welcomeMessage = t('welcome');
 *
 * // Change language
 * const handleLanguageChange = () => onChangeLang('fr');
 * ```
 */
export function useTranslate(ns?: string) {
    const { t, i18n } = useTranslation(ns);

    const [fallback] = allLangs.filter((lang) => lang.value === fallbackLng);

    const currentLang = allLangs.find((lang) => lang.value === i18n.resolvedLanguage);

    const onChangeLang = useCallback(
        async (newLang: LanguageValue) => {
            try {
                const langChangePromise = i18n.changeLanguage(newLang);

                const currentMessages = messages[newLang] || messages.en;

                toast.promise(langChangePromise, {
                    loading: currentMessages.loading,
                    success: () => currentMessages.success,
                    error: currentMessages.error,
                });

                if (currentLang) {
                    dayjs.locale(currentLang.adapterLocale);
                }
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error(error);
            }
        },
        [currentLang, i18n],
    );

    return {
        t,
        i18n,
        onChangeLang,
        currentLang: currentLang ?? fallback,
    };
}
